import React, { useState, useEffect, useRef, useContext } from "react";
import { toast } from "react-toastify";

import { makeStyles, useTheme, alpha } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import LinearProgress from "@material-ui/core/LinearProgress";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import GetAppIcon from "@material-ui/icons/GetApp";
import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";
import WarningIcon from "@material-ui/icons/Warning";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";

import LogoLoader from "../LogoLoader";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toneColor } from "../../pages/Financeiro/financeiroShared";
import {
  KINDS,
  kindOf,
  formatBytes,
  MAX_FILE_BYTES,
  WHATSAPP_VIDEO_BYTES,
  CAMPAIGN_STATUS,
  ACTIVE_CAMPAIGN,
  fileListUrl,
} from "../../helpers/fileLists";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    title: { paddingBottom: theme.spacing(1) },
    subtitle: { color: theme.palette.text.secondary, fontSize: 13, marginTop: 2 },
    content: { padding: 0, display: "flex", flexDirection: "column" },
    cols: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) 300px",
      flex: 1,
      minHeight: 0,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    main: { padding: theme.spacing(2, 3), minWidth: 0 },
    side: {
      borderLeft: `1px solid ${theme.palette.divider}`,
      backgroundColor: dark ? alpha("#fff", 0.03) : "#f6f8fb",
      padding: theme.spacing(2),
      minWidth: 0,
      [theme.breakpoints.down("sm")]: { borderLeft: "none", borderTop: `1px solid ${theme.palette.divider}` },
    },
    fields: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: theme.spacing(1.5),
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
      "& .MuiOutlinedInput-root": { borderRadius: 10 },
    },
    secTitle: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
      margin: theme.spacing(2.5, 0, 1.25),
      "& span": { textTransform: "none", letterSpacing: 0, fontWeight: 500 },
    },
    items: { display: "flex", flexDirection: "column", gap: 10 },
    item: {
      display: "grid",
      gridTemplateColumns: "52px minmax(0, 1fr) auto",
      gap: 12,
      alignItems: "start",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 12,
      padding: theme.spacing(1.25, 1.5),
    },
    itemMissing: {
      borderColor: toneColor(theme, "amber"),
      backgroundColor: alpha(toneColor(theme, "amber"), 0.08),
    },
    preview: {
      width: 52,
      height: 52,
      borderRadius: 9,
      display: "grid",
      placeItems: "center",
      fontSize: 11,
      fontWeight: 700,
      color: "#fff",
      overflow: "hidden",
      backgroundSize: "cover",
      backgroundPosition: "center",
    },
    previewMissing: {
      backgroundColor: "transparent",
      color: toneColor(theme, "amber"),
      border: `2px dashed ${toneColor(theme, "amber")}`,
    },
    meta: { minWidth: 0, display: "flex", flexDirection: "column", gap: 6 },
    fileName: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontWeight: 500,
      fontSize: 13.5,
      minWidth: 0,
      "& > span": { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
      "& small": { color: theme.palette.text.secondary, fontWeight: 400, whiteSpace: "nowrap" },
    },
    missingText: { color: toneColor(theme, "amber") },
    itemWarn: { fontSize: 12, color: toneColor(theme, "amber"), display: "flex", alignItems: "center", gap: 4 },
    caption: { "& .MuiOutlinedInput-root": { borderRadius: 10, fontSize: 13.5 } },
    itemActions: { display: "flex", flexDirection: "column" },
    drop: {
      border: `2px dashed ${theme.palette.divider}`,
      borderRadius: 12,
      padding: theme.spacing(2.25),
      textAlign: "center",
      color: theme.palette.text.secondary,
      cursor: "pointer",
      marginTop: 10,
      backgroundColor: dark ? alpha("#fff", 0.02) : "#f6f8fb",
      outline: "none",
      "&:focus-visible": { borderColor: theme.palette.primary.main },
      "& b": { color: theme.palette.primary.main },
      "& small": { display: "block", marginTop: 4, fontSize: 12 },
    },
    dropActive: {
      borderColor: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, 0.06),
    },
    banner: {
      display: "flex",
      gap: 10,
      alignItems: "flex-start",
      borderRadius: 10,
      padding: theme.spacing(1.25, 1.5),
      fontSize: 13,
      lineHeight: 1.45,
      marginTop: 10,
      backgroundColor: alpha(toneColor(theme, "amber"), 0.12),
    },
    wa: {
      backgroundColor: dark ? "#0b141a" : "#efeae2",
      borderRadius: 12,
      padding: theme.spacing(1.5, 1.25),
      display: "flex",
      flexDirection: "column",
      gap: 8,
      maxHeight: 480,
      overflowY: "auto",
    },
    bubble: {
      alignSelf: "flex-end",
      backgroundColor: dark ? "#144d37" : "#d9fdd3",
      borderRadius: "10px 10px 2px 10px",
      padding: 4,
      maxWidth: "88%",
      fontSize: 13,
      color: dark ? "#e9edef" : "#111b21",
      boxShadow: "0 1px 1px rgba(0,0,0,.08)",
    },
    bubbleMedia: {
      borderRadius: 7,
      height: 120,
      width: 200,
      maxWidth: "100%",
      display: "grid",
      placeItems: "center",
      color: "#fff",
      fontWeight: 700,
      backgroundSize: "cover",
      backgroundPosition: "center",
    },
    bubbleDoc: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      backgroundColor: "rgba(0,0,0,.06)",
      borderRadius: 7,
      padding: 8,
      minWidth: 180,
      "& > div": { minWidth: 0 },
    },
    bubbleDocIcon: {
      width: 30,
      height: 36,
      borderRadius: 4,
      display: "grid",
      placeItems: "center",
      color: "#fff",
      fontSize: 9,
      fontWeight: 700,
      flexShrink: 0,
    },
    bubbleCaption: { padding: "5px 6px 2px", lineHeight: 1.35, whiteSpace: "pre-wrap", wordBreak: "break-word" },
    bubbleTime: { textAlign: "right", fontSize: 10.5, opacity: 0.6, padding: "0 4px" },
    hint: { fontSize: 12, color: theme.palette.text.secondary },
    actions: {
      padding: theme.spacing(1.75, 3),
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: theme.spacing(1),
    },
    progress: { display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: theme.palette.text.secondary, flex: "1 1 220px" },
    progressBar: { width: 140, borderRadius: 99 },
    button: { textTransform: "none", fontWeight: 600, borderRadius: 10 },
  };
});

let keySeq = 0;
const nextKey = () => {
  keySeq += 1;
  return `it-${keySeq}`;
};

const fromServer = (fileListId, option) => ({
  key: nextKey(),
  id: option.id,
  caption: option.name || "",
  fileName: option.fileName || "",
  size: option.size,
  kind: kindOf(option.mediaType, option.fileName),
  missing: Boolean(option.missing),
  url: option.missing ? null : fileListUrl(fileListId, option.path),
  file: null,
  objectUrl: null,
});

const fromFile = (file) => {
  const kind = kindOf(file.type, file.name);
  return {
    key: nextKey(),
    id: null,
    caption: "",
    fileName: file.name,
    size: file.size,
    kind,
    missing: false,
    url: null,
    file,
    objectUrl: kind === "image" ? URL.createObjectURL(file) : null,
  };
};

const revoke = (items) =>
  items.forEach((it) => it.objectUrl && URL.revokeObjectURL(it.objectUrl));

const tooBig = (file) => {
  if (file.size <= MAX_FILE_BYTES) return false;
  toast.error(`"${file.name}" tem ${formatBytes(file.size)}. O limite é 64 MB por arquivo.`);
  return true;
};

const FileModal = ({ open, onClose, fileListId, reload }) => {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const { user } = useContext(AuthContext);

  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [items, setItems] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(null);
  const [touched, setTouched] = useState(false);
  const [dragging, setDragging] = useState(false);
  // Se a lista foi criada mas o envio dos arquivos falhou, tentar de novo
  // edita essa lista em vez de criar outra.
  const [savedId, setSavedId] = useState(null);

  const addInput = useRef(null);
  const swapInput = useRef(null);
  const swapKey = useRef(null);

  const listId = fileListId || savedId;

  useEffect(() => {
    if (!open) return;
    setName("");
    setMessage("");
    setItems([]);
    setCampaigns([]);
    setTouched(false);
    setProgress(null);
    setSavedId(null);
    if (!fileListId) return;

    let cancelled = false;
    (async () => {
      setLoadingData(true);
      try {
        const { data } = await api.get(`/files/${fileListId}`);
        if (cancelled) return;
        setName(data.name || "");
        setMessage(data.message || "");
        setItems((data.options || []).map((o) => fromServer(fileListId, o)));
        setCampaigns(data.campaigns || []);
      } catch (err) {
        toastError(err);
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, fileListId]);

  const handleClose = () => {
    if (saving) return;
    revoke(items);
    onClose();
  };

  // ── itens ──
  const addFiles = (fileList) => {
    const files = Array.from(fileList || []).filter((f) => !tooBig(f));
    if (!files.length) return;
    setItems((prev) => [...prev, ...files.map(fromFile)]);
  };

  const swapFile = (key, file) => {
    if (!file || tooBig(file)) return;
    setItems((prev) =>
      prev.map((it) => {
        if (it.key !== key) return it;
        if (it.objectUrl) URL.revokeObjectURL(it.objectUrl);
        const fresh = fromFile(file);
        return { ...fresh, key: it.key, id: it.id, caption: it.caption };
      })
    );
  };

  const removeItem = (key) => {
    setItems((prev) => {
      const it = prev.find((x) => x.key === key);
      if (it) revoke([it]);
      return prev.filter((x) => x.key !== key);
    });
  };

  const setCaption = (key, caption) =>
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, caption } : it)));

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  // ── validação ──
  const nameError = touched && name.trim().length < 3 ? "Use pelo menos 3 caracteres." : "";
  const missingCount = items.filter((it) => it.missing && !it.file).length;
  const canSave = !saving && !loadingData && name.trim().length >= 3 && missingCount === 0;

  // ── salvar ──
  const handleSave = async () => {
    setTouched(true);
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        message: message.trim(),
        userId: user?.id,
        options: items.map((it) => ({ id: it.id || undefined, name: it.caption })),
      };
      const { data } = listId
        ? await api.put(`/files/${listId}`, payload)
        : await api.post("/files", payload);
      const id = data.id;
      setSavedId(id);

      // Guarda os ids devolvidos: se o upload falhar, tentar de novo não
      // duplica os itens.
      const ids = data.optionIds || [];
      const withIds = items.map((it, i) => ({ ...it, id: ids[i] || it.id }));
      setItems(withIds);

      const pending = withIds.filter((it) => it.file);
      if (pending.length) {
        const form = new FormData();
        pending.forEach((it) => {
          form.append("optionId", String(it.id));
          form.append("files", it.file);
        });
        setProgress({ done: 0, total: pending.length, pct: 0 });
        await api.post(`/files/uploadList/${id}`, form, {
          onUploadProgress: (e) => {
            if (!e.total) return;
            const pct = Math.round((e.loaded / e.total) * 100);
            const done = Math.min(pending.length, Math.floor((pct / 100) * pending.length));
            setProgress({ done, total: pending.length, pct });
          },
        });
      }

      toast.success("Lista de arquivos salva.");
      revoke(withIds);
      if (typeof reload === "function") reload();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
      setProgress(null);
    }
  };

  // ── peças ──
  const thumbStyle = (it) => {
    const src = it.objectUrl || (it.kind === "image" ? it.url : null);
    return src
      ? { backgroundImage: `url("${src}")` }
      : { backgroundColor: KINDS[it.kind].color };
  };

  const active = campaigns.filter((c) => ACTIVE_CAMPAIGN.includes(c.status));
  const subtitle = listId
    ? active.length
      ? `Usada em ${active.length} campanha${active.length > 1 ? "s" : ""} ${CAMPAIGN_STATUS[active[0].status]}${active.length > 1 ? "s" : ""}: as alterações valem para os próximos envios.`
      : campaigns.length
      ? `Usada em ${campaigns.length} campanha${campaigns.length > 1 ? "s" : ""} já finalizada${campaigns.length > 1 ? "s" : ""}.`
      : "Ainda não usada em campanhas."
    : "Os arquivos são enviados em ordem, antes da mensagem da campanha.";

  const previewItems = items.filter((it) => !(it.missing && !it.file));

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={fullScreen} scroll="paper">
      {loadingData && <LogoLoader overlay />}
      <DialogTitle disableTypography className={classes.title}>
        <Typography variant="h6" component="h2">
          {fileListId ? "Editar lista de arquivos" : "Nova lista de arquivos"}
        </Typography>
        <div className={classes.subtitle}>{subtitle}</div>
      </DialogTitle>

      <DialogContent dividers className={classes.content}>
        <div className={classes.cols}>
          <div className={classes.main}>
            <div className={classes.fields}>
              <TextField
                label="Nome da lista"
                variant="outlined"
                size="small"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setTouched(true)}
                error={Boolean(nameError)}
                helperText={nameError || " "}
                inputProps={{ maxLength: 255 }}
                autoFocus={!fileListId}
              />
              <TextField
                label="Descrição interna (opcional)"
                variant="outlined"
                size="small"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                helperText="Só aparece aqui, não é enviada ao contato."
              />
            </div>

            <div className={classes.secTitle}>
              Arquivos, na ordem de envio
              <span>{items.length} {items.length === 1 ? "item" : "itens"}</span>
            </div>

            <div className={classes.items}>
              {items.map((it) => {
                const missing = it.missing && !it.file;
                const bigVideo = it.kind === "video" && it.size > WHATSAPP_VIDEO_BYTES;
                return (
                  <div key={it.key} className={`${classes.item} ${missing ? classes.itemMissing : ""}`}>
                    {missing ? (
                      <div className={`${classes.preview} ${classes.previewMissing}`}>?</div>
                    ) : (
                      <div className={classes.preview} style={thumbStyle(it)}>
                        {!(it.objectUrl || (it.kind === "image" && it.url)) && KINDS[it.kind].label}
                      </div>
                    )}
                    <div className={classes.meta}>
                      <div className={classes.fileName}>
                        {missing ? (
                          <span className={classes.missingText}>Nenhum arquivo escolhido</span>
                        ) : (
                          <>
                            <span title={it.fileName}>{it.fileName}</span>
                            {it.size != null && <small>{formatBytes(it.size)}</small>}
                            {it.file && it.id && <small>· novo</small>}
                          </>
                        )}
                      </div>
                      {bigVideo && (
                        <div className={classes.itemWarn}>
                          <WarningIcon style={{ fontSize: 14 }} />
                          Vídeo acima de 16 MB: o WhatsApp pode recusar.
                        </div>
                      )}
                      <TextField
                        className={classes.caption}
                        placeholder="Legenda (opcional)"
                        variant="outlined"
                        size="small"
                        multiline
                        minRows={1}
                        maxRows={4}
                        value={it.caption}
                        onChange={(e) => setCaption(it.key, e.target.value)}
                        inputProps={{ maxLength: 255, "aria-label": `Legenda de ${it.fileName || "item"}` }}
                        disabled={it.kind === "audio"}
                        helperText={it.kind === "audio" ? "Áudio é enviado sem legenda." : undefined}
                      />
                    </div>
                    <div className={classes.itemActions}>
                      <Tooltip title={missing ? "Escolher arquivo" : "Trocar arquivo"}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            swapKey.current = it.key;
                            swapInput.current.click();
                          }}
                        >
                          <AttachFileIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {it.url && !it.file && (
                        <Tooltip title="Baixar">
                          <IconButton size="small" component="a" href={it.url} target="_blank" rel="noopener noreferrer">
                            <GetAppIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Remover">
                        <IconButton size="small" onClick={() => removeItem(it.key)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                );
              })}
            </div>

            {missingCount > 0 && (
              <div className={classes.banner}>
                <WarningIcon style={{ color: toneColor(theme, "amber"), fontSize: 18 }} />
                <span>
                  {missingCount === 1 ? "Um item está" : `${missingCount} itens estão`} sem arquivo. Escolha o
                  arquivo ou remova o item para poder salvar.
                </span>
              </div>
            )}

            <div
              className={`${classes.drop} ${dragging ? classes.dropActive : ""}`}
              role="button"
              tabIndex={0}
              onClick={() => addInput.current.click()}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && addInput.current.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
            >
              <CloudUploadOutlinedIcon style={{ verticalAlign: "middle", marginRight: 6 }} />
              <b>Arraste arquivos aqui</b> ou clique para escolher
              <small>Vários de uma vez · imagem, vídeo, áudio, PDF e documentos · até 64 MB cada</small>
            </div>
            <input
              ref={addInput}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = "";
              }}
            />
            <input
              ref={swapInput}
              type="file"
              hidden
              onChange={(e) => {
                swapFile(swapKey.current, e.target.files[0]);
                e.target.value = "";
              }}
            />
          </div>

          <aside className={classes.side}>
            <div className={classes.secTitle} style={{ marginTop: 0 }}>Como chega no WhatsApp</div>
            <div className={classes.wa}>
              {previewItems.length === 0 && (
                <span className={classes.hint}>Adicione arquivos para ver a prévia.</span>
              )}
              {previewItems.map((it) => {
                const src = it.objectUrl || (it.kind === "image" ? it.url : null);
                const showCaption = it.caption && it.kind !== "audio";
                return (
                  <div key={it.key} className={classes.bubble}>
                    {it.kind === "image" || it.kind === "video" ? (
                      <div
                        className={classes.bubbleMedia}
                        style={src ? { backgroundImage: `url("${src}")` } : { backgroundColor: KINDS[it.kind].color }}
                      >
                        {it.kind === "video" ? <PlayArrowIcon fontSize="large" /> : !src && "IMG"}
                      </div>
                    ) : (
                      <div className={classes.bubbleDoc}>
                        <span className={classes.bubbleDocIcon} style={{ backgroundColor: KINDS[it.kind].color }}>
                          {KINDS[it.kind].label}
                        </span>
                        <div>
                          <div style={{ fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {it.fileName}
                          </div>
                          {it.size != null && <div className={classes.hint}>{formatBytes(it.size)}</div>}
                        </div>
                      </div>
                    )}
                    {showCaption && <div className={classes.bubbleCaption}>{it.caption}</div>}
                    <div className={classes.bubbleTime}>09:00 ✓✓</div>
                  </div>
                );
              })}
            </div>
            <div className={classes.hint} style={{ marginTop: 8 }}>
              A mensagem da campanha é enviada depois dos arquivos.
            </div>
          </aside>
        </div>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <div className={classes.progress}>
          {progress && (
            <>
              <LinearProgress variant="determinate" value={progress.pct} className={classes.progressBar} />
              Enviando {progress.total === 1 ? "arquivo" : `${progress.done} de ${progress.total} arquivos`}… {progress.pct}%
            </>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button className={classes.button} variant="outlined" onClick={handleClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            className={classes.button}
            variant="contained"
            color="primary"
            disableElevation
            onClick={handleSave}
            disabled={!canSave}
          >
            {saving ? "Salvando…" : fileListId ? "Salvar alterações" : "Criar lista"}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default FileModal;
